import type { InputType } from 'config'

/**
 * Mirrors `IHubspotMappingConfig` on the backend (`modules/config/hubspot-mapping-config.service`).
 * The sync is config-driven per tenant, so everything a tenant's HubSpot leads and contacts are
 * built from is on this screen.
 */
export interface IHubspotMappingConfig {
  enabled: boolean
  /**
   * Which HubSpot object a loan becomes.
   *
   * Leads need Sales Hub Professional or Enterprise and then have to be switched on in the account.
   * A portal without the object refuses every lead write with a 403 naming
   * `crm.objects.leads.write`, the same body HubSpot sends when the scope really is ungranted, so
   * the error points at a checkbox that is already ticked and nothing done there helps.
   */
  objectType: 'leads' | 'deals'
  /**
   * HubSpot property -> the expression that fills it, for the loan record.
   *
   * Named `lead` because that is the stored key, from before the object was a choice. It holds
   * whichever object `objectType` names, so on a deals tenant these are deal properties.
   */
  lead: Record<string, string>
  /** HubSpot contact property -> the expression that fills it. */
  contact: Record<string, string>
  /** Statuses whose change pushes an update. Empty means every status. */
  syncStatuses: string[]
  ownerSource: 'operator' | 'accountExecutive'
  ownerOnCreateOnly: boolean
  fallbackOwnerEmail: string
  loanLinkTemplate: string
  primaryContactAssociationTypeId: number
}

export interface IHubspotSourceKey {
  label: string
  type: 'text' | 'number' | 'date'
}

/** The `static` half of `GET /admin/config/hubspotMapping`: the same catalog the server resolves. */
export interface IHubspotMappingStatic {
  sourceKeys: Record<string, IHubspotSourceKey>
  grammar: string[]
  starter: { lead: Record<string, string>; contact: Record<string, string> }
  /** The same starter for a deals tenant: HubSpot's own deal properties at the top. */
  dealStarter: { lead: Record<string, string>; contact: Record<string, string> }
  objectTypes: string[]
  ownerSources: string[]
}

/**
 * What HubSpot last said about this tenant's access token.
 *
 * `active` and `readiness` below are read from the saved configuration, so they cannot tell a good
 * token from one HubSpot refuses. This can, and it outranks them in the banner: a complete mapping
 * on a dead token is the state that used to render as a green "On" while nothing was being written.
 */
export interface IHubspotCredential {
  state: 'ok' | 'rejected' | 'missing' | 'vendorOff' | 'unknown'
  /** HubSpot's own status line when it refused, e.g. `401 invalid token`. */
  detail?: string
  /**
   * 401 or 403, when the backend knows which.
   *
   * They take opposite remediation: a 401 token is replaced, a 403 token is kept and the app is
   * granted a scope. Collapsing them sent an admin hunting for a new token while the one they had
   * was working, so the banner branches on this rather than on `detail`.
   */
  status?: number
  /** The scopes this sync needs that the token does not carry. */
  missingScopes?: string[]
}

/** The `status` half: what this tenant resolves to right now, not something we save back. */
export interface IHubspotMappingStatus {
  active: boolean
  readiness: string[]
  credential?: IHubspotCredential
}

export const defaultHubspotMappingConfig = (): IHubspotMappingConfig => ({
  enabled: false,
  objectType: 'leads',
  lead: {},
  contact: {},
  syncStatuses: [],
  ownerSource: 'operator',
  ownerOnCreateOnly: false,
  fallbackOwnerEmail: '',
  loanLinkTemplate: '',
  primaryContactAssociationTypeId: 578,
})

export const objectTypeOptions: Record<string, string> = {
  leads: 'Leads',
  deals: 'Deals',
}

/**
 * HubSpot's association type id tying the loan record to its primary contact, per object.
 *
 * 578 is LEAD_TO_PRIMARY_CONTACT and 3 is DEAL_TO_CONTACT. The id names a PAIR of objects, so the
 * other object's id is not merely suboptimal: HubSpot rejects it, and the sync swallows that error,
 * which is why switching object has to move this value too.
 */
export const defaultAssociationTypeId = (objectType: 'leads' | 'deals'): number =>
  objectType === 'deals' ? 3 : 578

export const ownerSourceOptions: Record<string, string> = {
  operator: 'The user who made the change',
  accountExecutive: 'The loan Account Executive',
}

/**
 * The scalar settings, rendered through `RenderInput`.
 *
 * `statusOptions` comes from the tenant's own status labels, so the search index can call this
 * with none and still read every title. `objectType` only moves the wording, so the same default
 * keeps every title readable there too.
 */
export const settingInputs = (
  statusOptions: Record<string, string> = {},
  objectType: 'leads' | 'deals' = 'leads',
): Record<string, InputType> => ({
  syncSection: {
    title: 'Sync',
    inputType: 'section',
    span: 2,
  },
  enabled: {
    title: 'Sync loans to HubSpot',
    inputType: 'toggle',
    tooltip: 'Off leaves the integration dormant for this tenant, whatever is mapped below.',
    required: false,
  },
  objectType: {
    title: 'HubSpot object',
    inputType: 'select',
    options: objectTypeOptions,
    hasDefaultOption: false,
    required: false,
    tooltip:
      'Leads need Sales Hub Professional or Enterprise and have to be switched on in the account. A portal without them refuses every write with a missing-scope error that granting the scope cannot fix, so choose Deals there.',
  },
  syncStatuses: {
    title: 'Statuses that push an update',
    inputType: 'multiselect',
    options: statusOptions,
    value: [],
    required: false,
    tooltip: 'Leave every status unticked to push on all of them.',
  },
  ownerSection: {
    title: objectType === 'deals' ? 'Deal owner' : 'Lead owner',
    inputType: 'section',
    span: 2,
  },
  ownerSource: {
    title: 'Owner',
    inputType: 'select',
    options: ownerSourceOptions,
    hasDefaultOption: false,
    required: false,
  },
  ownerOnCreateOnly: {
    title: `Set the owner only when the ${objectType === 'deals' ? 'deal' : 'lead'} is created`,
    inputType: 'toggle',
    tooltip: 'On keeps the owner the rep it was assigned to, whoever edits the loan afterwards.',
    required: false,
  },
  fallbackOwnerEmail: {
    title: 'Fallback owner email',
    inputType: 'text',
    type: 'email',
    tooltip: `Used when the loan has no Account Executive, so a ${
      objectType === 'deals' ? 'deal' : 'lead'
    } is never left unowned.`,
    required: false,
  },
  advancedSection: {
    title: 'Advanced',
    inputType: 'section',
    span: 2,
  },
  loanLinkTemplate: {
    title: 'Loan link template',
    inputType: 'text',
    placeholder: 'https://app.example.com/loan/{loanNumber}',
    tooltip: 'Fills the loanLink source. {loanNumber} is replaced with the loan number.',
    required: false,
  },
  primaryContactAssociationTypeId: {
    title: 'Primary contact association type',
    inputType: 'text',
    type: 'number',
    tooltip:
      objectType === 'deals'
        ? "HubSpot's association type id for deal to contact. 3 unless HubSpot says otherwise."
        : "HubSpot's association type id for lead to primary contact. 578 unless HubSpot says otherwise.",
    required: false,
  },
})
